import React, { useState } from 'react'
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDate } from '../../utils/dateUtils';
import ImageUploadModal from '../../utils/ImageUploadModal';
import { IconButton, Popover } from '@mui/material';
import { CalendarDaysIcon, CheckIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';

const MERCARI_KEYWORDS = ['mercari'];
const isMercariOrder = (website) =>
  website && MERCARI_KEYWORDS.some((kw) => website.toLowerCase().includes(kw));

// Mercari Shops orders (vs normal items) — product/order lives on a shop URL.
const MERCARI_SHOP_URL = /mercari\.com\/shops?\/products?\/|mercari-shops\.com\/(?:products?|orders)\//i;
const isMercariShopUrl = (u) => !!u && MERCARI_SHOP_URL.test(u);

// ส่งตรวจ (Anshin appraisal) orders produce TWO Japanese tracking numbers at
// different times, but this row has one field. So it only ever asks for ONE leg,
// chosen by the appraisal's state, and is labelled for it — the admin can never
// put the appraisal number in the warehouse field or the reverse:
//
//   none    → the ส่งตรวจ number (seller → inspection centre)
//   passed  → the ordinary number (centre → our warehouse), as for any order
//
// sent / arrived / failed never reach this screen: the backend leaves them out,
// because there is nothing to enter while the centre has the parcel, and nothing
// will ever arrive after a failure.
//
// Read off the ORDER, so it works however the order was made — admin form, Beam
// link or the new platform all put a `mercari_anshin` line in extraJpFees. Match
// on `type`: the flows write different labels. `order.appraisal` appears only
// once the seller has shipped, so no row means "ask for the ส่งตรวจ number".
const ANSHIN_FEE_TYPE = 'mercari_anshin';

const appraisalLegOf = (order) => {
  const hasFee = Array.isArray(order.extraJpFees)
    && order.extraJpFees.some((fee) => fee?.type === ANSHIN_FEE_TYPE);
  const appraisal = order.appraisal;

  if (!appraisal) return hasFee ? 'appraisal' : null;
  // Once the parcel has gone to the centre the row decides, fee line or not.
  if (appraisal.status === 'passed') return 'after_appraisal';
  return null;
};

const TableRow = ({order, handleSave, handleSaveAppraisal, handleSavePreDate, handleStatusChange, handleCancel, currentTab = 'new'}) => {
  const { orderDate, url, website, CustomerProfile, smileNameInput, ShipmentStatusTrackings, preDate, orderByOption, transactionPageUrl } = order;
  const appraisalLeg = appraisalLegOf(order);
  const [isUpdated, setIsUpdated] = useState(false);
  const [isPreDateUpdated, setIsPreDateUpdated] = useState(false);
  const [tracking, setTracking] = useState(order.tracking || '');
  const [preOrderDate, setPreOrderDate] = useState(preDate ? new Date(preDate) : null);
  const [preDateAnchorEl, setPreDateAnchorEl] = useState(null);

  const isMercari = isMercariOrder(website);
  // presence of a hand-carry row = Air; absence = Ship (sea)
  const isAir = !!order.handCarry;
  const [notMercariBypass, setNotMercariBypass] = useState(false);
  const [mercariImages, setMercariImages] = useState([]);
  const [mercariImagePreviews, setMercariImagePreviews] = useState([]);
  const [showMercariImageModal, setShowMercariImageModal] = useState(false);

  // Get the relevant status tracking entry for the current tab
  const getCurrentStatusTracking = () => {
    if (!ShipmentStatusTrackings || !Array.isArray(ShipmentStatusTrackings)) return null;
    return ShipmentStatusTrackings.find(tracking => tracking.status === currentTab);
  };

  // Day thresholds: new=5, follow_1=5, follow_2=7, follow_3=9, pending_cancel=5
  const getDayThreshold = () => {
    const map = { new: 5, follow_1: 5, follow_2: 7, follow_3: 9, pending_cancel: 5 };
    return map[currentTab] ?? 5;
  };

  const calculateDaysPassed = () => {
    const today = new Date();
    let startDate;
    if (currentTab === 'new') {
      startDate = new Date(orderDate);
    } else {
      const currentTracking = getCurrentStatusTracking();
      startDate = currentTracking ? new Date(currentTracking.statusEnteredAt) : new Date(orderDate);
    }
    const diffTime = Math.abs(today - startDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const isOldOrder = () => {
    return calculateDaysPassed() >= getDayThreshold();
  };

  const handleTrackingChange = (e) => {
    setTracking(e.target.value);
  };

  const handleMercariImageConfirm = (files) => {
    if (files?.length > 0) {
      setMercariImages(files);
      setMercariImagePreviews(files.map((f) => URL.createObjectURL(f)));
    }
    setShowMercariImageModal(false);
  };

  const handleMercariImageClear = () => {
    mercariImagePreviews.forEach((url) => URL.revokeObjectURL(url));
    setMercariImages([]);
    setMercariImagePreviews([]);
  };

  const handleUpdateClick = async () => {
    if (!tracking) {
      toast.warning("Please input first!!");
      return;
    }
    if (isMercari && !notMercariBypass && mercariImages.length === 0) {
      toast.warning(
        appraisalLeg === 'appraisal'
          ? "กรุณาแนบรูปภาพที่แสดง Tracking No ส่งตรวจ (Mercari)"
          : "กรุณาแนบรูปภาพที่แสดงสินค้าและ Tracking No (Mercari)"
      );
      return;
    }

    // The ส่งตรวจ number goes to the order's appraisal, never to orders.trackingJp —
    // that field is waiting for the second parcel, centre → warehouse.
    if (appraisalLeg === 'appraisal') {
      const ok = await handleSaveAppraisal({
        orderId: order.id,
        trackingJp: tracking,
        images: notMercariBypass ? [] : mercariImages,
        notMercariBypass,
      });
      if (ok) setIsUpdated(true);
      return;
    }

    const updatedOrder = { ...order, trackingJp: tracking };
    handleSave(updatedOrder, mercariImages.length > 0 ? mercariImages : null, notMercariBypass);
    setIsUpdated(true);
  };

  const handlePreDatePopoverOpen = (e) => {
    setPreDateAnchorEl(e.currentTarget);
  };

  const handlePreDatePopoverClose = () => {
    setPreDateAnchorEl(null);
  };

  const handleUpdatePreDateClick = () => {
    if (preOrderDate && handleSavePreDate) {
      const updatedOrder = {
        ...order,
        isPre: true,
        preDate: preOrderDate,
      };
      handleSavePreDate(updatedOrder);
      setIsPreDateUpdated(true);
      handlePreDatePopoverClose();
    } else if (!preOrderDate) {
      toast.warning("Please select a pre-order date first!!");
    }
  };

  const preDatePopoverOpen = Boolean(preDateAnchorEl);

  // Copy cancellation info for the person who will process the cancellation (last stage only)
  const handleCopyCancellationInfo = () => {
    const smileOrderNoVal = smileNameInput || order.id || '—';
    const text = [
      `${smileOrderNoVal}`,
      `${url || '—'}`,
      `วันที่สั่งซื้อ: ${orderDate ? formatDate(orderDate) : '—'}`,
      'รบกวนยกเลิกรายการนี้',
    ].join('\n');
    navigator.clipboard.writeText(text).then(
      () => toast.success('Cancellation info copied to clipboard'),
      () => toast.error('Failed to copy')
    );
  };

  // Get row class name based on conditions
  const getRowClassName = () => {
    if (isUpdated) return 'border-t border-gray-200 bg-green-200';
    if (isPreDateUpdated) return 'border-t border-gray-200 bg-blue-100';
    if (isOldOrder()) return 'border-t border-gray-200 bg-yellow-100';
    return 'border-t border-gray-200';
  };

  const daysPassed = calculateDaysPassed();


  return (
    <>
    <tr className={getRowClassName()}>
      <td className='p-4'>{formatDate(orderDate)}</td>
      <td className='p-4'>{orderByOption?.label ?? '—'}</td>
      <td className='p-4'>
        <span 
          className={daysPassed >= getDayThreshold() ? 'text-red-600 font-semibold' : ''}
        >
          {daysPassed} วัน
        </span>
      </td>
      <td className='p-4'>{CustomerProfile.contact}</td>
      <td className='p-4'>{smileNameInput}</td>
      <td className='p-4'>
        <a href={url} className="text-blue-500 hover:underline truncate w-64 block" target="_blank" rel="noopener noreferrer">
          {url}
        </a>
        {isMercariShopUrl(url) && transactionPageUrl && (
          <a href={transactionPageUrl} className="text-emerald-600 hover:underline truncate w-64 block text-xs mt-1" target="_blank" rel="noopener noreferrer">
            🛍️ Transaction: {transactionPageUrl}
          </a>
        )}
      </td>
      <td className='p-4'>{website}</td>
      <td className='p-4'>
        {isAir ? (
          <span className="inline-block whitespace-nowrap rounded px-2 py-1 text-xs font-semibold bg-sky-100 text-sky-700">✈️ Air</span>
        ) : (
          <span className="inline-block whitespace-nowrap rounded px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-700">🚢 Ship</span>
        )}
      </td>
      <td className='p-4'>
        {/* Which leg this field is for, stated before the admin types — the two
            numbers look alike and only one is ever right for this row. */}
        {appraisalLeg === 'appraisal' && (
          <div className="mb-1 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">
            🔍 ส่งตรวจ · เลขพัสดุ ผู้ขาย → ศูนย์ตรวจ
          </div>
        )}
        {appraisalLeg === 'after_appraisal' && (
          <div className="mb-1 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-green-100 text-green-800 border border-green-300">
            ✓ ส่งตรวจผ่านแล้ว · เลขพัสดุ ศูนย์ตรวจ → โกดัง
          </div>
        )}
        <input
          type="text"
          placeholder={appraisalLeg === 'appraisal' ? 'เลขพัสดุส่งตรวจ (ไปศูนย์ตรวจ)' : 'Enter Japan Tracking'}
          onChange={handleTrackingChange}
          className={`border-2 p-2 rounded w-full focus:outline-none ${
            appraisalLeg === 'appraisal'
              ? 'border-amber-400 bg-amber-50 focus:border-amber-500'
              : 'border-gray-300 focus:border-blue-400'
          }`}
        />
        {isMercari && (
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {!notMercariBypass && (
              <>
                {mercariImagePreviews.length > 0 ? (
                  <div className="flex items-center gap-1">
                    {mercariImagePreviews.map((preview, idx) => (
                      <div key={idx} className="relative shrink-0">
                        <img
                          src={preview}
                          alt={`Mercari verify ${idx + 1}`}
                          className="w-8 h-8 object-cover rounded border border-orange-300"
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleMercariImageClear}
                      className="bg-red-500 text-white rounded-full p-px hover:bg-red-600"
                      title="ลบรูป"
                    >
                      <XMarkIcon className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowMercariImageModal(true)}
                    className="flex items-center gap-0.5 text-[10px] px-1 py-0.5 border border-dashed border-orange-400 text-orange-600 rounded hover:bg-orange-50"
                    title="แนบรูปสินค้า + Tracking No (สูงสุด 2 รูป)"
                  >
                    <PhotoIcon className="w-3 h-3" />
                    รูป
                  </button>
                )}
              </>
            )}
            <label className="inline-flex items-center gap-0.5 cursor-pointer text-[10px] text-gray-500 whitespace-nowrap">
              <input
                type="checkbox"
                checked={notMercariBypass}
                onChange={(e) => {
                  setNotMercariBypass(e.target.checked);
                  if (e.target.checked) handleMercariImageClear();
                }}
                className="rounded border-gray-300 w-3 h-3"
              />
              ไม่ใช่ Mercari / ไม่มีรูป
            </label>
          </div>
        )}
      </td>
      <td className='p-4'>
        <div className="flex items-center gap-1">
          <span className="text-sm min-w-[4rem]">{preDate ? formatDate(preDate) : '-'}</span>
          <IconButton
            size="small"
            onClick={handlePreDatePopoverOpen}
            title="Update pre-order date"
            className="!p-1"
          >
            <CalendarDaysIcon className="w-5 h-5 text-purple-500" />
          </IconButton>
          <Popover
            open={preDatePopoverOpen}
            anchorEl={preDateAnchorEl}
            onClose={handlePreDatePopoverClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <div className="p-3 flex flex-col gap-2">
              <DatePicker
                selected={preOrderDate}
                onChange={(date) => setPreOrderDate(date)}
                dateFormat="dd/MM/yyyy"
                inline
              />
              <IconButton
                size="small"
                onClick={handleUpdatePreDateClick}
                title="Save pre-order date"
                className="!bg-purple-500 !text-white hover:!bg-purple-600"
              >
                <CheckIcon className="w-5 h-5" />
              </IconButton>
            </div>
          </Popover>
        </div>
      </td>
      <td className='p-4'>
        <div className="flex flex-wrap gap-2 items-center">
          <button 
            onClick={handleUpdateClick}
            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
          >
            Update Tracking
          </button>
          {/* ตามแล้ว / ไปรอยกเลิก: new, follow_1, follow_2, follow_3 */}
          {['new', 'follow_1', 'follow_2', 'follow_3'].includes(currentTab) && (
            <button 
              onClick={() => handleStatusChange(order, currentTab || order.status)}
              className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
            >
              {currentTab === 'follow_3' ? 'ไปรอยกเลิก' : 'ตามแล้ว'}
            </button>
          )}
          {/* รอยกเลิก: เกิน 5 วัน แสดงปุ่ม ตามเรื่องอีกที */}
          {currentTab === 'pending_cancel' && daysPassed > 5 && (
            <button 
              onClick={() => handleStatusChange(order, currentTab)}
              className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
            >
              ตามเรื่องอีกที
            </button>
          )}
          <button 
            onClick={() => handleCancel(order, currentTab || order.status)}
            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
          >
            ยกเลิก
          </button>
          {/* Last stage (รอยกเลิก): copy cancellation info for the person who will process */}
          {currentTab === 'pending_cancel' && (
            <button
              onClick={handleCopyCancellationInfo}
              className="bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700"
              title="Copy cancellation info for the person who will process"
            >
              Copy ข้อความยกเลิก
            </button>
          )}
        </div>
      </td>
    </tr>

    {isMercari && (
      <ImageUploadModal
        isOpen={showMercariImageModal}
        onRequestClose={() => setShowMercariImageModal(false)}
        onConfirm={handleMercariImageConfirm}
        title="แนบรูปสินค้า + Tracking No (Mercari) — สูงสุด 2 รูป"
        maxFiles={2}
      />
    )}
    </>
  )
}

export default TableRow